import {
  CompletedActivityMetadata,
  CompletedActivityPayloadType,
  FocusSummarySearchProps,
  LogQuantityAnswer,
  RoutineActivitiesStatsSummary,
  ToDoPlayerTask,
  UserRank
} from 'interfaces';
import {
  AiToneOptions,
  ENTITLEMENT,
  FEED_BACK_QUESTION,
  FONT_OPTION,
  LanguageCode,
  Language,
  TO_DO_STATUS
} from 'enums';
import { v4 as uuid } from 'uuid';
import {
  ACTIVITY_TYPES,
  DEFAULT_FEEDBACK_CHOICES,
  DEFAULT_TODO_TASK_DURATION,
  EISENHOWER_QUADRANT,
  EISENHOWER_QUADRANT_OPTIONS,
  EMPTY_STRING,
  FIRST_ACTIVITY_INDEX,
  FIRST_SETTING_TAB_INDEX,
  FOCUS_SUMMARY,
  FONTS,
  FORMAT_ABBR_MONTH_DAY_YEAR,
  FORMAT_ABBR_MONTH_YEAR,
  FORMAT_ABBR_YEAR,
  HABIT_CHOICE_TYPE,
  INITIAL_COMPETENCY_LEVEL,
  ITEM_INDEX_NOT_FOUND,
  MODAL_TYPES,
  PLATFORMS,
  PLAYER_TIME_LABEL_FORMATS,
  PRIORITY_OPTION,
  ROUTINE,
  ROUTINE_DESTINATION_POSITION,
  ROUTINE_SOURCE,
  SCREEN,
  SURVEY_QUESTION_CATEGORY,
  TAB,
  TIME_FORMAT_24H,
  TIME_UNIT_CONVERT_FACTOR,
  TIMEOUT_DELAY
} from 'constants/general';
import { STATS } from 'constants/table';
import moment from 'moment';
import Quotes from 'constants/quotes';
import {
  ActivityType,
  ChoiceType,
  FocusModeTemplateType,
  HabitPackType,
  ToDoTask
} from 'interfaces/common.interface';
import {
  AnswerChoice,
  FeedbackType,
  RoutinePlayerType,
  SettingsType,
  CustomRoutineType
} from 'interfaces/setting.interface';
import {
  DailySummaryStatsType,
  WeeklySummaryStatsTypes,
  WeeklyFocusBlockSummary,
  UserOnboardingStats
} from 'interfaces/user.interface';
import { SettingSlice } from 'interfaces/initial-states.interface';
import { isCurrentPlayingActivityFromChoice } from '../validators';
import { t } from 'services/i18n.service';
import parser from 'html-react-parser';
import { stripHtml } from 'string-strip-html';
import { DEFAULT_TO_DO_TASK_STATUS } from 'assets/data';
import { decode } from 'html-entities';
import DOMPurify from 'dompurify';
import 'moment-timezone';
import { IFRAME } from 'constants/tag';
import { SUBSCRIPTION_PLAN } from 'constants/billingInfo';
import {
  addMinutesToTime,
  convertSecondsToWholeMinutes,
  formatDateToYMD
} from 'utils/time.util';
import { NODE_ENV } from 'constants/node-env';

export const getYoutubeURLId = (url: string) => {
  const match = url.match(
    /^(?:(?:https?:)?\/\/)?(?:www\.)?(?:m\.)?(?:youtu(?:be)?\.com\/(?:v\/|embed\/|shorts\/|watch(?:\/|\?v=))|youtu\.be\/)((?:\w|-){11})(?:\S+)?$/
  );
  return match && String(match[1]).length === 11 ? match[1] : '';
};

export const oneDecimalPlacesIfNotWholeNumber = (value: number) =>
  value % 1 !== 0 ? Number(value.toFixed(1)) : value;

export const getPackIds = (installedPacks: HabitPackType[]) => {
  return installedPacks.map((pack) => pack.id);
};

export const packSearchHandler = (
  habitPacks: HabitPackType[],
  searchWord: string
) => {
  const packsToReturn: HabitPackType[] = habitPacks.filter((pack) => {
    if (
      pack.pack_name.toLowerCase().includes(searchWord.toLowerCase()) ||
      String(pack?.creator_name ?? EMPTY_STRING)
        .toLowerCase()
        .includes(searchWord.toLowerCase())
    ) {
      return pack;
    } else if (searchWord === '') {
      return habitPacks;
    } else {
      return false;
    }
  });
  return packsToReturn;
};

export const focusTemplateSearchHandler = (
  focusTemplates: FocusModeTemplateType[],
  searchWord: string
) => {
  const focusTemplatesToReturn: FocusModeTemplateType[] = focusTemplates.filter(
    (focusTemplate) => {
      if (
        focusTemplate.name?.toLowerCase().includes(searchWord.toLowerCase()) ||
        focusTemplate.author_name
          ?.toLowerCase()
          .includes(searchWord.toLowerCase())
      ) {
        return focusTemplate;
      } else if (searchWord === '') {
        return focusTemplates;
      } else {
        return false;
      }
    }
  );
  return focusTemplatesToReturn;
};

export const copyPackLink = (packId: string) => {
  window.navigator.clipboard.writeText(
    process.env.REACT_APP_SHARE_PACK_LINK + packId
  );
};

export const copyFocusTemplateLink = (templateId: string) => {
  window.navigator.clipboard.writeText(
    process.env.REACT_APP_SHARE_FOCUS_TEMPLATE_LINK + templateId
  );
};

export const updateWeeklySummaryStats = (
  weeklySummaryStats: WeeklySummaryStatsTypes[],
  payload: {
    finish_time: string;
    quantity_logged?: number;
    start_time?: string;
  }[],
  isFocusModeSummary = true
) =>
  weeklySummaryStats.map((stat) => {
    let counter = 0;
    for (const item of payload) {
      // For focus sessions, prefer start_time so that sessions starting near
      // midnight are counted against the day they actually began rather than
      // the following day (which finish_time might fall on).
      // Activity completions only carry finish_time, so we fall back to that.
      const timestamp =
        isFocusModeSummary && 'start_time' in item && item.start_time
          ? item.start_time
          : item.finish_time;
      if (!timestamp) continue;
      if (moment(timestamp).isSame(moment(stat.logged_date), 'day')) {
        counter++;
      }
    }
    return isFocusModeSummary
      ? {
          ...stat,
          completed_focus_modes: counter
        }
      : {
          ...stat,
          completed_activities: counter
        };
  });


export const calculateSequenceDuration = (activities: ActivityType[]) => {
  return activities.reduce(
    (total, currentActivity) => total + currentActivity.duration_seconds,
    0
  );
};

export const determineRoutineNextStartTime = (
  timeZone: string,
  routineTime: string | undefined
) => {
  const currentTime = moment.tz(timeZone);

  // Handle the undefined case
  if (!routineTime) {
    return currentTime.toDate();
  }

  // Parse the routine time (assumes 12-hour format 'hh:mm')
  const [hourStr, minuteStr] = routineTime.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  // Create a moment object for today's routine time in the given timezone
  const startTimeForToday = moment
    .tz(timeZone)
    .set({ hour, minute, second: 0, millisecond: 0 });

  // Compare current time with today's routine time
  const isPastRoutineStartTimeForToday = currentTime.isAfter(startTimeForToday);

  // If the time has passed, return tomorrow's routine time
  return isPastRoutineStartTimeForToday
    ? startTimeForToday.add(1, 'day').toDate()
    : startTimeForToday.toDate();
};

export const nextLevel = (current_level: number) =>
  current_level === 0 ? 1 : ++current_level;

export const previousLevel = (current_level: number) =>
  current_level === 0 || current_level === 1 ? 1 : --current_level;

export const getActivitiesSortedByPriority = (activities: ActivityType[]) => {
  const highPriorityActivities = activities.filter(
    (activity) => activity?.priority === PRIORITY_OPTION.HIGH
  );
  const standardPriorityActivities = activities.filter(
    (activity) => activity?.priority !== PRIORITY_OPTION.HIGH
  );
  return [...highPriorityActivities, ...standardPriorityActivities];
};

export const hasNonHighPriorityEveningActivities = (
  evening_activities?: ActivityType[]
): boolean => {
  return (
    evening_activities?.some(
      (activity) => activity.priority !== PRIORITY_OPTION.HIGH
    ) ?? false
  );
};

export const handleRandomChoice = (activity: ActivityType) => {
  const choices = activity?.choices ?? [];
  if (choices.length) {
    const randomIndex = Math.floor(Math.random() * choices.length);
    return {
      ...activity,
      ...choices[randomIndex]
    };
  } else {
    return activity;
  }
};

export const handleCompetencyBasedChoice = (activity: ActivityType) => {
  const choices = activity?.choices ?? [];
  if (choices.length) {
    const level =
      activity?.current_competency_level ?? INITIAL_COMPETENCY_LEVEL;
    const choice = choices.find((choice) => choice.competency_level === level);
    return {
      ...activity,
      ...choice
    };
  } else {
    return activity;
  }
};

export const getRoutineActivitiesOfCurrentSettings = (
  type: string,
  currentSettings: SettingsType,
  customRoutinePosition?: number
) => {
  const TYPE_TO_ACTIVITY_MAPPING = {
    [ACTIVITY_TYPES.MORNING]: currentSettings.morning_activities ?? [],
    [ACTIVITY_TYPES.EVENING]: currentSettings.evening_activities ?? [],
    [ACTIVITY_TYPES.BREAKING]: currentSettings.break_activities ?? [],
    [ACTIVITY_TYPES.STANDALONE]: currentSettings.standalone_activities ?? [],
    [ACTIVITY_TYPES.CUSTOM_ROUTINES]:
      customRoutinePosition !== undefined
        ? (currentSettings.custom_routines?.[customRoutinePosition]
            .standalone_activities ?? [])
        : []
  };
  return TYPE_TO_ACTIVITY_MAPPING[type];
};

export const getYoutubeEmbeddedURL = (url: string) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const videoId = getYoutubeURLId(url);
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&controls=0&modestbranding=1&disablekb=1&loop=1&origin=${encodeURIComponent(origin)}`;
};

export const getActivityIndexFromCurrentSettings = (
  type: string,
  currentSettings: SettingsType,
  activityId: string
) => {
  const routineActivities = getRoutineActivitiesOfCurrentSettings(
    type,
    currentSettings
  );
  const index =
    routineActivities?.findIndex((activity) => {
      const choices = activity?.choices ?? [];
      return choices.length > 0
        ? choices.some((choice) => choice.id === activityId)
        : activity.id === activityId;
    }) ?? -1;
  return index;
};

export const getActivitiesOfSettings = (
  type: string,
  settings: SettingSlice,
  customRoutinePosition?: number
) => {
  const activity = getRoutineActivitiesOfCurrentSettings(
    type,
    settings.currentSettings,
    customRoutinePosition
  );
  const TYPE_TO_ACTIVITY_MAPPING = {
    [ACTIVITY_TYPES.LIBRARY]: settings.activityLibrary ?? []
  };
  return activity ?? TYPE_TO_ACTIVITY_MAPPING[type];
};

export const updateRoutineInSettings = ({
  state,
  isCustom,
  routineType,
  activities,
  customRoutineIndex
}: {
  state: { currentSettings: SettingsType };
  isCustom: boolean;
  routineType: string;
  activities: ActivityType[];
  customRoutineIndex?: number;
}) => {
  if (isCustom && typeof customRoutineIndex === 'number') {
    state.currentSettings.custom_routines =
      state.currentSettings.custom_routines?.map(
        (routine: CustomRoutineType, idx: number) =>
          idx === customRoutineIndex
            ? { ...routine, standalone_activities: activities }
            : routine
      );
  } else {
    state.currentSettings = {
      ...state.currentSettings,
      [routineType]: activities
    };
  }
};

export const getNextActivity = (
  routinePlayerActivities: RoutinePlayerType,
  currentPlayingActivity: ActivityType | null
) => {
  const index = routinePlayerActivities.activities.findIndex((activity) => {
    const choices = activity?.choices ?? [];
    if (choices.length) {
      const choiceIndex = choices.findIndex(
        (choice) => choice.id === currentPlayingActivity?.id
      );
      return choiceIndex !== ITEM_INDEX_NOT_FOUND;
    } else {
      return activity.id === currentPlayingActivity?.id;
    }
  });
  const nextIndex = increment(index);
  const isCurrentActivityNotLastInSequence =
    index !== ITEM_INDEX_NOT_FOUND &&
    nextIndex < routinePlayerActivities.activities.length;
  if (isCurrentActivityNotLastInSequence) {
    let nextActivity = routinePlayerActivities.activities[nextIndex];
    if (nextActivity.choices?.length) {
      const isRandomChoiceType =
        nextActivity.choice_type === HABIT_CHOICE_TYPE.RANDOM;
      const isCompetencyChoiceType =
        nextActivity.choice_type === HABIT_CHOICE_TYPE.COMPETENCY_BASED;
      if (isRandomChoiceType) {
        nextActivity = handleRandomChoice(nextActivity);
      } else if (isCompetencyChoiceType) {
        nextActivity = handleCompetencyBasedChoice(nextActivity);
      }
    }
    return nextActivity;
  }
  // handle routine being completed
  return null;
};

export const getCompletedActivityPayload = (
  currentPlayingActivity: ActivityType,
  deviceId: string,
  routinePlayerActivities: RoutinePlayerType,
  log_quantity_answers: LogQuantityAnswer[],
  note: string | undefined,
  metadata?: CompletedActivityMetadata
) => {
  let completed_activity_payload: CompletedActivityPayloadType = {
    activity_id: currentPlayingActivity.id,
    duration_logged: currentPlayingActivity.duration_seconds,
    device_id: deviceId,
    activity_sequence_id: currentPlayingActivity.activity_sequence_id,
    start_time: moment()
      .subtract(currentPlayingActivity.duration_seconds, 'seconds')
      .toDate(),
    finish_time: moment().toDate(),
    metadata: metadata ?? {
      is_skipped: false,
      skipped_did_not_complete: false,
      skipped_did_complete: false
    },
    log_quantity_answers
  };

  const { parent_activity_id, choice_id } = isCurrentPlayingActivityFromChoice(
    routinePlayerActivities.activities,
    currentPlayingActivity
  );
  // create payload for activity with choices
  if (choice_id && parent_activity_id) {
    completed_activity_payload = {
      ...completed_activity_payload,
      activity_id: parent_activity_id,
      choice_id
    };
  } else if (parent_activity_id) {
    // create payload for an activity without a choices
    completed_activity_payload = {
      ...completed_activity_payload,
      activity_id: parent_activity_id
    };
  } else {
    completed_activity_payload = {
      ...completed_activity_payload,
      activity_id: currentPlayingActivity?.id ?? EMPTY_STRING
    };
  }

  if (note) {
    completed_activity_payload = {
      ...completed_activity_payload,
      note_logged: note
    };
  }

  return completed_activity_payload;
};

export const getCurrentPlatformFont = (
  platform: string,
  font?: FONT_OPTION
) => {
  if (font) {
    switch (font) {
      case FONT_OPTION.ARIAL:
        return FONTS.ARIAL;
      case FONT_OPTION.SEGEO_UI:
        return FONTS.SEGOE_UI;
      case FONT_OPTION.SAN_FRANCISCO:
        return FONTS.SAN_FRANCISCO;
      case FONT_OPTION.OPEN_DYSLEXIC:
        return FONTS.OPEN_DYSLEXIC;
      case FONT_OPTION.ROBOTO:
        return FONTS.ROBOTO;
      case FONT_OPTION.COMIC_SANS_MS:
        return FONTS.COMIC_SANS;
      case FONT_OPTION.HELVETICA:
        return FONTS.HELVETICA;
      case FONT_OPTION.INTEL_ONE_MONO:
        return FONTS.INTEL_ONE_MONO;
      case FONT_OPTION.CENTURY_GOTHIC:
        return FONTS.CENTURY_GOTHIC;
      case FONT_OPTION.VERDANA:
        return FONTS.VERDANA;
      case FONT_OPTION.TAHOMA:
        return FONTS.TAHOMA;
      case FONT_OPTION.TREBUCHET_MS:
        return FONTS.TREBUCHET_MS;
      case FONT_OPTION.POPPINS:
        return FONTS.POPPINS;
      default:
        return FONTS.SANS;
    }
  } else {
    // it supports older versions of the desktop app
    switch (platform) {
      case PLATFORMS.WIN:
        return FONTS.SEGOE_UI;
      case PLATFORMS.MAC:
        return FONTS.SAN_FRANCISCO;
      default:
        return FONTS.SANS;
    }
  }
};

export const getModalUrls = (
  modalType: string,
  activity: ActivityType,
  position: number,
  choices: ChoiceType[]
) => {
  let currentUrls: string[] = [];
  if (modalType === MODAL_TYPES.VIDEOS_URLS) {
    currentUrls = activity.video_urls ?? [];
  } else if (modalType === MODAL_TYPES.CHOICE_VIDEO_URLS) {
    currentUrls = choices[position].video_urls ?? [];
  } else {
    currentUrls = activity?.allowed_urls ?? [];
  }
  return currentUrls;
};

export const getActivityDuration = (habit: ActivityType) =>
  habit.duration_seconds >= 60
    ? t('time_minutes', {
        count: Math.trunc(habit.duration_seconds / 60)
      })
    : t('time_seconds', { count: habit.duration_seconds });

export const increment = (value: number, step?: number) =>
  step ? value + step : ++value;

export const decrement = (value: number, step?: number) =>
  step ? value - step : --value;

export const getRandomQuote = () => {
  const position = Math.floor(Math.random() * Quotes.length);
  return Quotes[position];
};

/**
 * Returns the effective focus duration in seconds for a focus block.
 * Prefers wall-clock time (finish_time - start_time) over the stored
 * focus_duration_seconds when the wall-clock value is >= the stored value,
 * which handles the case where the user extended the session but the native
 * app did not update focus_duration_seconds accordingly.
 */
export const getEffectiveFocusDurationSeconds = (
  focusBlock: WeeklyFocusBlockSummary
): number => {
  const stored = focusBlock.focus_duration_seconds ?? 0;
  if (focusBlock.start_time && focusBlock.finish_time) {
    const wallClockSeconds = Math.round(
      (new Date(focusBlock.finish_time).getTime() -
        new Date(focusBlock.start_time).getTime()) /
        1000
    );
    if (wallClockSeconds >= stored) {
      return wallClockSeconds;
    }
  }
  return stored;
};

export const getFocusSummaryFilteredData = (
  position: number,
  language: string,
  search: FocusSummarySearchProps,
  focusBlocks: WeeklyFocusBlockSummary[]
) => {
  switch (position) {
    case FOCUS_SUMMARY.COLUMN.DURATION:
      return focusBlocks.filter((focusBlock) =>
        moment
          .duration(
            getEffectiveFocusDurationSeconds(focusBlock),
            FOCUS_SUMMARY.TIME_UNIT
              .SECOND as moment.unitOfTime.DurationConstructor
          )
          .locale(language)
          .humanize()
          ?.toLowerCase()
          .includes(search.word.toLowerCase())
      );
    case FOCUS_SUMMARY.COLUMN.START_TIME:
      return focusBlocks.filter((focusBlock) =>
        search.date
          ? moment(search.date).isSame(
              moment(focusBlock?.start_time),
              FOCUS_SUMMARY.TIME_UNIT.DAY as moment.unitOfTime.StartOf
            )
          : true
      );
    case FOCUS_SUMMARY.COLUMN.FINISH_TIME:
      return focusBlocks.filter((focusBlock) =>
        search.date
          ? moment(search.date).isSame(
              moment(focusBlock?.finish_time),
              FOCUS_SUMMARY.TIME_UNIT.DAY as moment.unitOfTime.StartOf
            )
          : true
      );
    case FOCUS_SUMMARY.COLUMN.INTENTION:
      return focusBlocks.filter((focusBlock) =>
        focusBlock?.intention?.toLowerCase().includes(search.word.toLowerCase())
      );
    case FOCUS_SUMMARY.COLUMN.DISTRACTION:
      return focusBlocks.filter((focusBlock) =>
        focusBlock?.distractions
          ?.toLowerCase()
          .includes(search.word.toLowerCase())
      );
    default:
      return focusBlocks.filter((focusBlock) =>
        focusBlock?.focus_mode?.name
          .toLowerCase()
          .includes(search.word.toLowerCase())
      );
  }
};

export const getFocusSummarySortValue = (
  focusBlock: WeeklyFocusBlockSummary,
  sortColumn: string
): string | number => {
  const focusSummarySortKeys = STATS.FOCUS_SUMMARY.WEEKLY.KEY;
  if (!focusSummarySortKeys.includes(sortColumn)) {
    return '';
  }
  if (sortColumn === focusSummarySortKeys[FOCUS_SUMMARY.COLUMN.DURATION]) {
    return getEffectiveFocusDurationSeconds(focusBlock);
  }
  if (sortColumn === focusSummarySortKeys[FOCUS_SUMMARY.COLUMN.FOCUS_BLOCK]) {
    return focusBlock.focus_mode?.name?.toLowerCase() ?? '';
  }
  const value = focusBlock[sortColumn as keyof WeeklyFocusBlockSummary];
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase();
  }
  return '';
};

export const sortFocusSummariesByColumn = (
  focusBlocks: WeeklyFocusBlockSummary[],
  sortColumn: string
): WeeklyFocusBlockSummary[] =>
  [...focusBlocks].sort((rowA, rowB) => {
    const valA = getFocusSummarySortValue(rowA, sortColumn);
    const valB = getFocusSummarySortValue(rowB, sortColumn);
    if (valA === valB) {
      return 0;
    }
    return valA > valB ? -1 : 1;
  });

export const getFocusSummaryLabelsFormat = (
  format: moment.unitOfTime.StartOf
) => {
  if (format === FOCUS_SUMMARY.TIME_UNIT.MONTH) {
    return FORMAT_ABBR_MONTH_YEAR;
  } else if (format === FOCUS_SUMMARY.TIME_UNIT.YEAR) {
    return FORMAT_ABBR_YEAR;
  } else {
    return FORMAT_ABBR_MONTH_DAY_YEAR;
  }
};

export const getFocusSummaryGraphLabelsAndData = (
  weeklyFocusBlockSummary: WeeklyFocusBlockSummary[],
  format: moment.unitOfTime.StartOf,
  length: number
) => {
  const labels: string[] = [];
  const data: number[] = [];

  const formatType = getFocusSummaryLabelsFormat(format);
  weeklyFocusBlockSummary.forEach((focusBlock) => {
    !labels.includes(moment(focusBlock?.created_at).format(formatType)) &&
      labels.push(moment(focusBlock.created_at).format(formatType));
  });
  labels.forEach((month, index) => {
    let totalMinutes = 0;
    weeklyFocusBlockSummary.forEach((focusBlock) => {
      if (moment(month).isSame(focusBlock?.created_at, format)) {
        totalMinutes += getEffectiveFocusDurationSeconds(focusBlock);
      }
    });
    data[index] = totalMinutes;
  });

  return {
    labels: labels.reverse().slice(0, length),
    data: data.reverse().slice(0, length)
  };
};

/**
 * Merge `daily_summary` API data into a weekly stats array using a date-keyed
 * Map. This ensures that days the API omits (zero-data gap days) receive an
 * empty summary rather than shifting every subsequent day's stats by one slot,
 * which was the root cause of bug #2742.
 *
 * @param weeklyStats        Base stats array (oldest-first, one entry per day)
 * @param focusBlockStats    Parallel array with completed_focus_modes per slot
 * @param dailySummary       API response; may have fewer entries than weeklyStats
 */
export const mergeDailySummary = (
  weeklyStats: WeeklySummaryStatsTypes[],
  focusBlockStats: { completed_focus_modes: number }[],
  dailySummary: DailySummaryStatsType[]
): WeeklySummaryStatsTypes[] => {
  const byDate = new Map(
    dailySummary.map((item) => [formatDateToYMD(item.date), item])
  );
  return weeklyStats.map((stat, index) => {
    const dateKey = formatDateToYMD(stat.logged_date);
    const summaryForDay = byDate.get(dateKey) ?? ({} as DailySummaryStatsType);
    return {
      ...stat,
      ...summaryForDay,
      completed_focus_modes: focusBlockStats[index]?.completed_focus_modes ?? 0
    };
  });
};

export const initWeeklySummaryStats = (days: number) =>
  Array.from({ length: days })
    .map((_, idx) => {
      return {
        logged_date: moment().subtract(idx, 'days').toDate(),
        completed_focus_modes: 0,
        completed_activities: 0,
        date: '',
        day_of_week: '',
        morning_percentage: 0,
        evening_percentage: 0,
        focus_modes: 0,
        morning_minutes: 0,
        morning_total_minutes: 0,
        evening_minutes: 0,
        evening_total_minutes: 0,
        micro_breaks_completion_percentage: 0,
        micro_breaks_minutes: 0,
        micro_breaks_total_minutes: 0,
        total_hours_spent_in_focus_sessions: 0
      };
    })
    .reverse();

export const getRoutineStatsSummaryGraphLabelsAndData = (
  routineStatusSummary: RoutineActivitiesStatsSummary[],
  timeRange: { startDate: Date | null; endDate: Date | null }
) => {
  const updatedSummaries = routineStatusSummary
    ?.filter((summary) => {
      if (timeRange.startDate && timeRange.endDate) {
        return moment(summary.date).isBetween(
          timeRange.startDate,
          timeRange.endDate,
          'day',
          '[]'
        );
      } else {
        return moment().isSame(summary.date, 'day');
      }
    })
    ?.map((routineSummary) => ({
      labels: moment(routineSummary?.date).local(true).format('MMM DD, YYYY'),
      data: parseInt(routineSummary.summary ?? '0')
    }));

  return {
    labels: updatedSummaries?.map((summary) => summary.labels)?.reverse(),
    data: updatedSummaries?.map((summary) => summary.data)?.reverse()
  };
};

export const getToDoDefaultStatusLabel = (
  status: string,
  externalStatuses: { label: string; status_id: string }[] = []
) => {
  const external = externalStatuses.find(({ label }) => label === status);
  if (external) {
    return external.label;
  }
  switch (status) {
    case TO_DO_STATUS.NOT_STARTED:
      return t('to_do_procrastinate.not_started');
    case TO_DO_STATUS.IN_PROGRESS:
      return t('to_do_procrastinate.in_progress');
    case TO_DO_STATUS.COMPLETED:
      return t('to_do_procrastinate.completed');
    default:
      return status;
  }
};

export const getPageBoundaries = (
  currentPage: number,
  itemsPerPage: number
) => {
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const pageEndIndex = currentPage * itemsPerPage;
  return { pageStartIndex, pageEndIndex };
};

export const getRandomAiTone = () => {
  const tones = Object.values(AiToneOptions);
  // Reject the top of the byte range that doesn't divide evenly by tones.length
  // so `% tones.length` stays uniform instead of modulo-biased (CWE-327).
  const maxUnbiased = 256 - (256 % tones.length);
  const randomArray = new Uint8Array(1);
  let byte = 0;
  do {
    byte = window.crypto.getRandomValues(randomArray)[0];
  } while (byte >= maxUnbiased);
  return tones[byte % tones.length];
};

/** Maps setting locales (`GB`/`ES`) and BCP-47 tags to API language codes (`en`/`es`). */
export const getApiLanguageCode = (language: string): LanguageCode => {
  const normalized = language.toLowerCase();
  if (
    normalized === LanguageCode.ES ||
    normalized.startsWith(`${LanguageCode.ES}-`)
  ) {
    return LanguageCode.ES;
  }
  return LanguageCode.EN;
};

export const getMotivationalSummaryLangQueryParam = (lang: string) =>
  getApiLanguageCode(lang) === LanguageCode.ES
    ? Language.SPANISH
    : Language.ENGLISH;

export const convertToSeconds = (minutes: number, seconds?: number) =>
  minutes * TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS + (seconds ?? 0);

/** Splits total seconds into whole minutes and remaining seconds (0–59). */
export const splitDurationSeconds = (
  totalSeconds?: number | null
): { minutes: number; seconds: number } => {
  const total = totalSeconds ?? 0;
  return {
    minutes: Math.floor(total / TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS),
    seconds: total % TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS
  };
};

/** Maps TimeDuration `{ hours, minutes, seconds }` to total duration in seconds. */
export const timeDurationPartsToSeconds = (time: {
  hours?: number;
  minutes: number;
  seconds: number;
}): number =>
  (time.hours ?? 0) * TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS +
  convertToSeconds(time.minutes, time.seconds);

export const convertToMinutes = (
  seconds?: number,
  minutes?: number,
  isFractionAllowed?: boolean
) => {
  const convertedMinutes =
    (seconds ?? 0) / TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS;
  return isFractionAllowed
    ? convertedMinutes
    : Math.floor(convertedMinutes) + (minutes ?? 0);
};

export const getValidNumber = (value: string | number) => {
  if (typeof value === 'number') {
    return value;
  } else {
    return isNaN(parseInt(value)) ? 0 : parseInt(value);
  }
};

export const getValidFocusSummaryColumnValue = (value?: string) => {
  if (!value) return '-';
  try {
    return decodeURIComponent(value.replaceAll('+', ' '));
  } catch {
    return decode(value.replaceAll('+', ' '));
  }
};

export const adminSubscriptionStatusOptions = Object.values(ENTITLEMENT).map(
  (entitlement) => ({
    label: String(t(`entitlement.${entitlement}`)).replace(':', ''),
    value: entitlement
  })
);

export const getTodayDateWithOutTime = () =>
  new Date().toISOString().split('T')[0];

export const getNewHabitProps = (
  tabs: string[],
  activeTabIndex: number,
  {
    morning_activities,
    break_activities,
    evening_activities,
    standalone_activities,
    custom_routines
  }: SettingsType,
  platform: string,
  customRoutine?: string,
  routines?: { title: string; value: boolean }[],
  isCustomRoutinesTab?: boolean,
  customRoutinePosition?: number
) => {
  if (isCustomRoutinesTab) {
    return [
      ACTIVITY_TYPES.CUSTOM_ROUTINES,
      customRoutinePosition !== undefined
        ? (custom_routines?.slice()?.[customRoutinePosition]
            ?.activity_sequence_id ?? uuid())
        : uuid()
    ];
  } else {
    if (customRoutine === ROUTINE.SUGGESTION) {
      const isMorningRoutineSelected = routines?.some(
        (routine) =>
          routine.value &&
          routine.title === t('routine_suggestion.morning_routine')
      );

      return activeTabIndex === FIRST_SETTING_TAB_INDEX &&
        (ROUTINE.MORNING === customRoutine || isMorningRoutineSelected)
        ? [
            ACTIVITY_TYPES.MORNING,
            morning_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
              ?.activity_sequence_id ?? uuid()
          ]
        : [
            ACTIVITY_TYPES.EVENING,
            evening_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
              ?.activity_sequence_id ?? uuid()
          ];
    } else {
      let newHabitProps = [
        ACTIVITY_TYPES.STANDALONE,
        standalone_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
          ?.activity_sequence_id ?? uuid()
      ];
      const isMobile = [
        PLATFORMS.ANDROID,
        PLATFORMS.IOS,
        PLATFORMS.MOB
      ].includes(platform);
      if (tabs[activeTabIndex] === TAB.MORNING) {
        newHabitProps = [
          ACTIVITY_TYPES.MORNING,
          morning_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
            ?.activity_sequence_id ?? uuid()
        ];
      } else if (!isMobile && tabs[activeTabIndex] === TAB.MICRO_BREAKS) {
        newHabitProps = [
          ACTIVITY_TYPES.BREAKING,
          break_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
            ?.activity_sequence_id ?? uuid()
        ];
      } else if (
        isMobile ||
        [TAB.TIMING, TAB.EVENING].includes(tabs[activeTabIndex])
      ) {
        newHabitProps = [
          ACTIVITY_TYPES.EVENING,
          evening_activities?.slice()?.[FIRST_ACTIVITY_INDEX]
            ?.activity_sequence_id ?? uuid()
        ];
      }
      return newHabitProps;
    }
  }
};

export const getEisenhowerQuadrantInfo = (quadrant: string) => {
  switch (quadrant) {
    case EISENHOWER_QUADRANT.DO:
      return {
        category: t('to_do_procrastinate.urgent_and_important'),
        decision: t('to_do_procrastinate.do')
      };
    case EISENHOWER_QUADRANT.DECIDE:
      return {
        category: t('to_do_procrastinate.important_but_not_urgent'),
        decision: t('to_do_procrastinate.decide')
      };
    case EISENHOWER_QUADRANT.DELEGATE:
      return {
        category: t('to_do_procrastinate.not_important_and_urgent'),
        decision: t('to_do_procrastinate.delegate')
      };
    default:
      return {
        category: t('to_do_procrastinate.not_important_and_not_urgent'),
        decision: t('to_do_procrastinate.delete')
      };
  }
};

export const getToDoUpdateTask = (task?: ToDoTask) => {
  const quadrant = EISENHOWER_QUADRANT_OPTIONS.find(
    (option) => option.value === task?.eisenhower_quadrant?.toString()
  );
  const eisenhower_quadrant = quadrant
    ? { label: quadrant.label, value: quadrant.value }
    : { label: EMPTY_STRING, value: EMPTY_STRING };
  const tags = (task?.tags ?? []).map((tag) => ({
    label: tag.text,
    value: tag.id
  }));
  return {
    id: task?.id ?? uuid(),
    status: task?.status ?? TO_DO_STATUS.NOT_STARTED,
    title: task?.title ?? EMPTY_STRING,
    details: task?.details ?? EMPTY_STRING,
    due_date:
      moment(task?.due_date).format('YYYY-MM-DD') ??
      moment().format('YYYY-MM-DD'),
    eisenhower_quadrant,
    tags,
    subtasks: task?.subtasks ?? [],
    objective: task?.objective ?? ''
  };
};

export const createHtml = (value?: string) =>
  value
    ? parser(
        DOMPurify.sanitize(value, {
          USE_PROFILES: { html: true }
        })
      )
    : EMPTY_STRING;

export const removeHtmlTags = (value?: string) =>
  value ? stripHtml(value).result : EMPTY_STRING;

export const getSortedLeaders = (usersRanks: UserRank[], column?: string) => {
  switch (column) {
    case t('leader_board.morning_streak'):
      return usersRanks.sort(
        (a, b) =>
          parseInt(b.morning_percent_number_day_of_stats_completed) -
            parseInt(a.morning_percent_number_day_of_stats_completed) ||
          parseInt(b.morning_routines_streak) -
            parseInt(a.morning_routines_streak)
      );
    case t('leader_board.break_streak'):
      return usersRanks.sort(
        (a, b) =>
          parseInt(b.micro_breaks_streak) - parseInt(a.micro_breaks_streak)
      );
    case t('leader_board.evening_streak'):
      return usersRanks.sort(
        (a, b) =>
          parseInt(b.evening_percent_number_day_of_stats_completed) -
            parseInt(a.evening_percent_number_day_of_stats_completed) ||
          parseInt(b.evening_routines_streak) -
            parseInt(a.evening_routines_streak)
      );
    case t('leader_board.focus_mode'):
      return usersRanks.sort(
        (a, b) =>
          parseInt(b.focus_modes_streak) - parseInt(a.focus_modes_streak)
      );
    default:
      return usersRanks;
  }
};

export const getBooleanGoalsCurrentSteak = (
  label: string,
  onboardingStats: UserOnboardingStats
) => {
  if (label === t('stats_page.chat_with_focus_bear')) {
    return onboardingStats?.has_chatted_with_focus_bear ?? false;
  } else if (label === t('stats_page.setup_a_focus_mode')) {
    return onboardingStats?.has_edited_focus_mode ?? false;
  }
  return false;
};

export const formatToDoPlayerTaskTimeConstraints = (
  tasks: ToDoPlayerTask[]
) => {
  let previous = tasks?.length ? tasks[0] : null;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return tasks?.map((task, index) => {
    const updatedTask = index
      ? {
          ...task,
          startTime: moment
            .tz(
              previous?.endTime ?? '',
              PLAYER_TIME_LABEL_FORMATS,
              userTimezone
            )
            .format('h:mm a'),
          endTime: moment
            .tz(
              previous?.endTime ?? '',
              PLAYER_TIME_LABEL_FORMATS,
              userTimezone
            )
            .add(parseInt(String(task.duration)), 'seconds')
            .format('h:mm a')
        }
      : {
          ...task,
          startTime: moment().tz(userTimezone).format('h:mm a'),
          endTime: moment()
            .tz(userTimezone)
            .add(parseInt(String(task.duration)), 'seconds')
            .format('h:mm a')
        };

    previous = updatedTask;
    return updatedTask;
  });
};

export const escapeSpecialEntities = (value: string) => {
  const specialAsciChars = [
    { symbol: '&amp;', value: '%26' },
    { symbol: '&lt;', value: '<' },
    { symbol: '&gt;', value: '>' }
  ];
  specialAsciChars.forEach((char) => {
    value = value.replaceAll(char.symbol, char.value);
  });
  return value;
};

export const getTodoTaskStatus = (task?: ToDoTask | null) => {
  return task?.external_statuses?.length
    ? task?.external_statuses.map((status) => ({
        label: status.label,
        value: uuid()
      }))
    : DEFAULT_TO_DO_TASK_STATUS;
};

export const getFeedbackChoices = (feedback: FeedbackType) => {
  let choices: AnswerChoice[] = [];
  if (feedback.type === FEED_BACK_QUESTION.BOOLEAN) {
    choices = (feedback?.options ?? [t('yes'), t('no')]).map((option) => ({
      checked: false,
      value: option
    }));
  } else if (feedback.type === FEED_BACK_QUESTION.NUMBER) {
    choices =
      feedback.options?.map((option) => ({ checked: false, value: option })) ??
      Array.from({ length: DEFAULT_FEEDBACK_CHOICES }).map((_, idx) => ({
        checked: false,
        value: String(idx)
      }));
  }
  return choices;
};

export const getTodoPlayerTaskDuration = (task_duration?: number | string) => {
  const parsedDuration = Number(task_duration);
  if (!isNaN(parsedDuration) && parsedDuration > 0) {
    return parsedDuration;
  }
  // Fallback when desktop does not send per-task duration (older app releases).
  return DEFAULT_TODO_TASK_DURATION;
};

export const isTaskExistsInToDoPlayer = (id: string, tasks: ToDoPlayerTask[]) =>
  tasks.some((playerTask) => playerTask.id === id);

export const getRoutineActivities = (activity_type: string) => {
  switch (activity_type) {
    case ACTIVITY_TYPES.MORNING:
      return ROUTINE_SOURCE.morning_activities;
    case ACTIVITY_TYPES.BREAKING:
      return ROUTINE_SOURCE.break_activities;
    case ACTIVITY_TYPES.CUSTOM_ROUTINES:
      return ROUTINE_SOURCE.custom_activities;
    default:
      return ROUTINE_SOURCE.evening_activities;
  }
};

export const getMoveActivityDestinationLabel = (position: string) => {
  switch (position) {
    case ROUTINE_DESTINATION_POSITION.START:
      return t('move_habit.start_of_the_routine');
    case ROUTINE_DESTINATION_POSITION.MIDDLE:
      return t('move_habit.middle_of_the_routine');
    default:
      return t('move_habit.end_of_the_routine');
  }
};

export const isProduction = process.env.NODE_ENV === NODE_ENV.PRODUCTION;

export const getYouTubePlayerHeight = (width: number) => {
  if (width >= SCREEN.WIDTH.DOUBLE_EXTRA_LARGE) {
    return IFRAME.HEIGHT.DOUBLE_EXTRA_LARGE;
  } else if (width >= SCREEN.WIDTH.EXTRA_LARGE) {
    return IFRAME.HEIGHT.EXTRA_LARGE;
  } else if (width >= SCREEN.WIDTH.LARGE) {
    return IFRAME.HEIGHT.LARGE;
  } else if (width >= SCREEN.WIDTH.TABLET) {
    return IFRAME.HEIGHT.TABLET;
  } else if (width >= SCREEN.WIDTH.SMALL) {
    return IFRAME.HEIGHT.SMALL;
  } else {
    return IFRAME.HEIGHT.DEFAULT;
  }
};

export const getSurveyCategoryLabel = (value: string) => {
  switch (value) {
    case SURVEY_QUESTION_CATEGORY.CHOICES_NUMBER:
      return t('admin.checkbox_with_text');
    case SURVEY_QUESTION_CATEGORY.CHOICES_BOOLEAN:
      return t('admin.radio_button_with_text');
    case SURVEY_QUESTION_CATEGORY.TEXT_AND_RATING:
      return t('admin.text_and_rating');
    default:
      return t('admin.text_only');
  }
};

export const getSubscriptionPlanTitle = (
  productIdentifier: string,
  options?: { isSchool?: boolean }
) => {
  if (isTeamSubscriptionPlan(productIdentifier)) {
    return t(
      options?.isSchool
        ? 'subscription_page.focus_bear_school_subscription'
        : 'subscription_page.focus_bear_team_subscription'
    );
  } else if (productIdentifier === SUBSCRIPTION_PLAN.PERSONAL_ANNUAL) {
    return t('subscription_page.personal_annual_subscription');
  } else if (productIdentifier === SUBSCRIPTION_PLAN.LIFE_TIME) {
    return t('subscription_page.personal_lifetime_subscription');
  } else {
    return productIdentifier.includes('trial') ? t('trial') : productIdentifier;
  }
};

export const filterOutCutoffTimeAndTutorial = (
  morning_activities: ActivityType[],
  break_activities: ActivityType[]
) => {
  return [
    morning_activities.map(
      ({ cutoff_time_for_doing_activity: _cutoff, ...rest }) => ({
        ...rest
      })
    ),
    break_activities.map(
      ({
        cutoff_time_for_doing_activity: _cutoff,
        tutorial: _tutor,
        ...rest
      }) => ({
        ...rest
      })
    )
  ];
};

export const getDurationFormat = (durationAsSecond: number) =>
  durationAsSecond >= TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS
    ? 'h[h]:mm[m]:ss[s]'
    : 'mm[m]:ss[s]';

export const isUserInternalTest = (email: string): boolean =>
  /^internaltest(\+[\w-]+)?@focusbear\.io$/.test(email);

export const isAlphaNumericInput = (value?: string) =>
  /^[a-zA-Z0-9\s]*$/.test(value ?? '');

export const isTeamSubscriptionPlan = (entitlement_product_id: string) =>
  // Use prefix match to cover current and future rc_promo_team_* variants (e.g. rc_promo_team_member_custom)
  entitlement_product_id.startsWith('rc_promo_team') ||
  [SUBSCRIPTION_PLAN.TEAM, SUBSCRIPTION_PLAN.TEAM_SIZE_5].includes(
    entitlement_product_id
  );

export const safeStringify = (data: unknown) =>
  typeof data === 'string' ? data : JSON.stringify(data);

export const runAfterDelay = (
  callback?: () => unknown,
  delay?: number
): NodeJS.Timeout => {
  return setTimeout(() => callback?.(), delay ?? TIMEOUT_DELAY.DEFAULT);
};

export const toFixedNumber = (value: number, digits = 1) =>
  Number(value.toFixed(digits));

export const toInt = (value: string | number, radix = 10) => {
  const parsed = parseInt(String(value), radix);
  return isNaN(parsed) ? undefined : parsed;
};

export const getOS = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/windows/.test(userAgent)) {
    return PLATFORMS.WIN;
  }
  if (/mac/.test(userAgent)) {
    return PLATFORMS.MAC;
  }
  if (/android/.test(userAgent)) {
    return PLATFORMS.ANDROID;
  }
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return PLATFORMS.IOS;
  }
  return EMPTY_STRING;
};

export const removeLeadingZerosFromNumbers = (str: string) => {
  return str.replace(/\b0+(\d+)/g, '$1');
};

export const isMobileUA = /iPhone|iPad|Android/i.test(navigator.userAgent);

export const getInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const getEarliestStandardPriorityActivityTime = (
  shutdown_time?: string,
  evening_activities?: ActivityType[]
): string | undefined => {
  if (!shutdown_time || !evening_activities?.length) {
    return undefined;
  }

  let cumulativeDuration = 0;
  let earliestTime: string | undefined;

  for (const activity of evening_activities) {
    const activityStartTime = addMinutesToTime(
      shutdown_time,
      convertSecondsToWholeMinutes(cumulativeDuration)
    );

    // Check if this activity has STANDARD priority
    if (activity.priority === PRIORITY_OPTION.STANDARD) {
      if (
        !earliestTime ||
        moment(activityStartTime, TIME_FORMAT_24H).isBefore(
          moment(earliestTime, TIME_FORMAT_24H)
        )
      ) {
        earliestTime = activityStartTime;
      }
    }

    // Add this activity's duration to cumulative
    cumulativeDuration += activity.duration_seconds ?? 0;
  }

  return earliestTime;
};

export const getTimeWheelPickerInitialValue = (
  timeValue: string | undefined,
  defaultTime: string
) => (timeValue && timeValue !== '00:00' ? timeValue : defaultTime);
